import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Voice Commands Component for Skybot
 * Hands-free sky scanning with voice control + Audio Feedback
 * 
 * Supported Commands:
 * - "Capture" / "Take photo" / "Snap" - Captures current frame
 * - "Record" / "Start recording" - Starts video recording
 * - "Stop" / "Stop recording" - Stops recording
 * - "Zoom in" / "Zoom out" - Adjusts zoom level
 * - "Zoom [1-200]" / "Zoom 50x" - Sets specific zoom level
 * - "Solar" / "Solar filter" / "Sun filter" - Toggles solar filter
 * - "Night" / "Night mode" / "Night vision" - Toggles night filters
 * - "Thermal" / "Heat vision" - Toggles thermal filter
 * - "Infrared" / "IR" - Toggles infrared filter
 * - "Reset" / "Clear filters" - Removes all filters
 * - "Analyze" / "Scan sky" - Triggers AI analysis
 * - "Fullscreen" / "Full screen" - Opens fullscreen mode
 * - "Switch camera" / "Flip camera" - Switches front/rear camera
 * - "Help" / "Commands" - Lists available commands
 */

// Audio feedback system using Web Audio API
class AudioFeedback {
  constructor() {
    this.audioContext = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.warn('Audio feedback not available:', e);
    }
  }

  // Generate a beep sound with specific frequency and duration
  beep(frequency = 440, duration = 0.15, type = 'sine', volume = 0.3) {
    if (!this.audioContext) this.init();
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (e) {
      console.warn('Audio beep error:', e);
    }
  }

  // Command recognized - pleasant double beep
  commandRecognized() {
    this.beep(880, 0.1, 'sine', 0.2);
    setTimeout(() => this.beep(1100, 0.15, 'sine', 0.25), 100);
  }

  // Capture sound - camera shutter simulation
  capture() {
    this.beep(2000, 0.05, 'square', 0.15);
    setTimeout(() => this.beep(800, 0.08, 'square', 0.1), 50);
  }

  // Recording start - ascending tones
  recordStart() {
    this.beep(440, 0.1, 'sine', 0.2);
    setTimeout(() => this.beep(660, 0.1, 'sine', 0.25), 120);
    setTimeout(() => this.beep(880, 0.15, 'sine', 0.3), 240);
  }

  // Recording stop - descending tones
  recordStop() {
    this.beep(880, 0.1, 'sine', 0.3);
    setTimeout(() => this.beep(660, 0.1, 'sine', 0.25), 120);
    setTimeout(() => this.beep(440, 0.15, 'sine', 0.2), 240);
  }

  // Zoom sound - pitch changes with direction
  zoom(direction = 'in') {
    if (direction === 'in') {
      this.beep(600, 0.08, 'triangle', 0.2);
      setTimeout(() => this.beep(900, 0.1, 'triangle', 0.25), 80);
    } else {
      this.beep(900, 0.08, 'triangle', 0.25);
      setTimeout(() => this.beep(600, 0.1, 'triangle', 0.2), 80);
    }
  }

  // Filter toggle - soft click
  filterToggle(active = true) {
    if (active) {
      this.beep(1200, 0.06, 'sine', 0.15);
      setTimeout(() => this.beep(1500, 0.08, 'sine', 0.2), 60);
    } else {
      this.beep(1500, 0.06, 'sine', 0.2);
      setTimeout(() => this.beep(1200, 0.08, 'sine', 0.15), 60);
    }
  }

  // Analyze/scan - sci-fi scanner sound
  analyze() {
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        this.beep(400 + (i * 200), 0.1, 'sawtooth', 0.1);
      }, i * 100);
    }
  }

  // Error sound - low warning tone
  error() {
    this.beep(200, 0.2, 'square', 0.2);
    setTimeout(() => this.beep(180, 0.3, 'square', 0.15), 200);
  }

  // Success sound - pleasant chime
  success() {
    this.beep(523, 0.1, 'sine', 0.2); // C5
    setTimeout(() => this.beep(659, 0.1, 'sine', 0.2), 100); // E5
    setTimeout(() => this.beep(784, 0.15, 'sine', 0.25), 200); // G5
  }

  // Listening activated - high ping
  listeningOn() {
    this.beep(1000, 0.08, 'sine', 0.2);
    setTimeout(() => this.beep(1500, 0.12, 'sine', 0.25), 80);
  }

  // Listening deactivated - low ping
  listeningOff() {
    this.beep(1500, 0.08, 'sine', 0.25);
    setTimeout(() => this.beep(1000, 0.12, 'sine', 0.2), 80);
  }

  // Switch camera - whoosh effect
  switchCamera() {
    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        this.beep(300 + (i * 100), 0.03, 'sine', 0.1 + (i * 0.02));
      }, i * 20);
    }
  }

  // Fullscreen - expanding sound
  fullscreen() {
    this.beep(400, 0.1, 'triangle', 0.15);
    setTimeout(() => this.beep(600, 0.1, 'triangle', 0.2), 100);
    setTimeout(() => this.beep(800, 0.15, 'triangle', 0.25), 200);
  }

  // Reset - refresh sound
  reset() {
    this.beep(800, 0.05, 'sine', 0.2);
    setTimeout(() => this.beep(600, 0.05, 'sine', 0.15), 50);
    setTimeout(() => this.beep(800, 0.1, 'sine', 0.2), 100);
  }
}

// Create singleton audio feedback instance
const audioFeedback = new AudioFeedback();

// Text-to-Speech feedback
const speak = (text, rate = 1.2) => {
  if ('speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = 1.1;
    utterance.volume = 0.8;
    
    // Try to use a robotic/digital sounding voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Google') || 
      v.name.includes('Samantha') ||
      v.name.includes('Alex')
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  }
};

const VoiceCommands = ({
  onCapture,
  onStartRecording,
  onStopRecording,
  isRecording,
  onZoomIn,
  onZoomOut,
  setZoomLevel,
  zoomLevel,
  MAX_ZOOM = 200,
  toggleFilter,
  filters,
  onAnalyze,
  onFullscreen,
  onSwitchCamera,
  onReset,
  isPremium,
  isOwner
}) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [voiceFeedbackEnabled, setVoiceFeedbackEnabled] = useState(true);
  const recognitionRef = useRef(null);
  const isUnlocked = isPremium || isOwner;

  // Check if Web Speech API is supported
  const isSpeechSupported = typeof window !== 'undefined' && 
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize Speech Recognition
  useEffect(() => {
    if (!isSpeechSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      setTranscript(interimTranscript || finalTranscript);
      
      if (finalTranscript) {
        processCommand(finalTranscript.toLowerCase().trim());
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        if (audioEnabled) audioFeedback.error();
        toast.error('Microphone access denied. Please enable it in settings.');
        setIsListening(false);
      }
    };
    
    recognition.onend = () => {
      // Restart if still listening mode
      if (isListening && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.log('Recognition restart error:', e);
        }
      }
    };
    
    recognitionRef.current = recognition;
    
    // Initialize audio context on component mount
    audioFeedback.init();
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isSpeechSupported]);

  // Play audio feedback helper
  const playFeedback = useCallback((type, ...args) => {
    if (!audioEnabled) return;
    audioFeedback[type]?.(...args);
  }, [audioEnabled]);

  // Speak feedback helper
  const speakFeedback = useCallback((text) => {
    if (!voiceFeedbackEnabled) return;
    speak(text);
  }, [voiceFeedbackEnabled]);

  // Process voice command with audio feedback
  const processCommand = useCallback((command) => {
    setLastCommand(command);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2000);
    
    // Command recognized sound
    playFeedback('commandRecognized');
    
    // Capture commands
    if (command.includes('capture') || command.includes('take photo') || 
        command.includes('snap') || command.includes('shoot')) {
      onCapture?.();
      playFeedback('capture');
      speakFeedback('Photo captured');
      toast.success('📸 Photo captured!');
      return;
    }
    
    // Recording commands
    if (command.includes('start recording') || (command.includes('record') && !command.includes('stop'))) {
      if (!isRecording) {
        onStartRecording?.();
        playFeedback('recordStart');
        speakFeedback('Recording started');
        toast.success('🔴 Recording started');
      }
      return;
    }
    
    if (command.includes('stop recording') || command.includes('stop record') || 
        (command.includes('stop') && isRecording)) {
      if (isRecording) {
        onStopRecording?.();
        playFeedback('recordStop');
        speakFeedback('Recording stopped');
        toast.success('⏹️ Recording stopped');
      }
      return;
    }
    
    // Zoom commands
    if (command.includes('zoom in') || command.includes('closer') || command.includes('enhance')) {
      onZoomIn?.();
      playFeedback('zoom', 'in');
      const newZoom = Math.min(zoomLevel * 1.5, MAX_ZOOM);
      speakFeedback(`Zoomed to ${newZoom.toFixed(0)}x`);
      toast.success(`🔍 Zoomed to ${newZoom.toFixed(1)}x`);
      return;
    }
    
    if (command.includes('zoom out') || command.includes('further') || command.includes('wide')) {
      onZoomOut?.();
      playFeedback('zoom', 'out');
      const newZoom = Math.max(zoomLevel / 1.5, 1);
      speakFeedback(`Zoomed to ${newZoom.toFixed(0)}x`);
      toast.success(`🔍 Zoomed to ${newZoom.toFixed(1)}x`);
      return;
    }
    
    // Specific zoom level
    const zoomMatch = command.match(/zoom\s*(\d+)/);
    if (zoomMatch) {
      const level = parseInt(zoomMatch[1]);
      if (level >= 1 && level <= MAX_ZOOM) {
        setZoomLevel?.(level);
        playFeedback('zoom', level > zoomLevel ? 'in' : 'out');
        speakFeedback(`Zoom set to ${level}x`);
        toast.success(`🔍 Zoom set to ${level}x`);
      } else {
        playFeedback('error');
        speakFeedback(`Invalid zoom level. Range is 1 to ${MAX_ZOOM}`);
        toast.error(`Zoom must be between 1 and ${MAX_ZOOM}`);
      }
      return;
    }

    // Maximum zoom
    if (command.includes('max zoom') || command.includes('maximum zoom') || command.includes('full zoom')) {
      if (isUnlocked) {
        setZoomLevel?.(MAX_ZOOM);
        playFeedback('zoom', 'in');
        speakFeedback(`Maximum zoom ${MAX_ZOOM}x`);
        toast.success(`🔍 Max zoom: ${MAX_ZOOM}x`);
      } else {
        playFeedback('error');
        speakFeedback('Maximum zoom requires premium');
        toast.error('Max zoom requires Premium');
      }
      return;
    }
    
    // Filter commands
    if (command.includes('solar') || command.includes('sun filter') || command.includes('sun dim')) {
      const wasActive = filters?.sun_dimming;
      toggleFilter?.('sun_dimming');
      playFeedback('filterToggle', !wasActive);
      speakFeedback(wasActive ? 'Solar filter off' : 'Solar filter on');
      toast.success(wasActive ? '☀️ Solar filter OFF' : '☀️ Solar filter ON');
      return;
    }
    
    if (command.includes('night mode') || command.includes('night filter') || 
        (command.includes('night') && !command.includes('vision'))) {
      const wasActive = filters?.night_mode;
      toggleFilter?.('night_mode');
      playFeedback('filterToggle', !wasActive);
      speakFeedback(wasActive ? 'Night mode off' : 'Night mode on');
      toast.success(wasActive ? '🌙 Night mode OFF' : '🌙 Night mode ON');
      return;
    }
    
    if (command.includes('night vision') || command.includes('vision mode')) {
      if (isUnlocked) {
        const wasActive = filters?.night_vision;
        toggleFilter?.('night_vision');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Night vision off' : 'Night vision activated');
        toast.success(wasActive ? '👁️ Night vision OFF' : '👁️ Night vision ON');
      } else {
        playFeedback('error');
        speakFeedback('Night vision requires premium');
        toast.error('Night vision requires Premium');
      }
      return;
    }
    
    if (command.includes('thermal') || command.includes('heat') || command.includes('temperature')) {
      if (isUnlocked) {
        const wasActive = filters?.thermal;
        toggleFilter?.('thermal');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Thermal off' : 'Thermal imaging on');
        toast.success(wasActive ? '🔥 Thermal OFF' : '🔥 Thermal ON');
      } else {
        playFeedback('error');
        speakFeedback('Thermal requires premium');
        toast.error('Thermal requires Premium');
      }
      return;
    }
    
    if (command.includes('infrared') || command.includes('ir filter') || command.includes('i r')) {
      if (isUnlocked) {
        const wasActive = filters?.infrared;
        toggleFilter?.('infrared');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Infrared off' : 'Infrared on');
        toast.success(wasActive ? '📡 Infrared OFF' : '📡 Infrared ON');
      } else {
        playFeedback('error');
        speakFeedback('Infrared requires premium');
        toast.error('Infrared requires Premium');
      }
      return;
    }
    
    if (command.includes('stars') || command.includes('star enhance') || command.includes('star filter')) {
      if (isUnlocked) {
        const wasActive = filters?.star_enhancement;
        toggleFilter?.('star_enhancement');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Star enhancement off' : 'Star enhancement on');
        toast.success(wasActive ? '⭐ Star enhancement OFF' : '⭐ Star enhancement ON');
      } else {
        playFeedback('error');
        speakFeedback('Star enhancement requires premium');
        toast.error('Star enhancement requires Premium');
      }
      return;
    }
    
    if (command.includes('clarity') || command.includes('clear') || command.includes('sharpen') || 
        command.includes('noise') || command.includes('denoise')) {
      const wasActive = filters?.noise_reduction;
      toggleFilter?.('noise_reduction');
      playFeedback('filterToggle', !wasActive);
      speakFeedback(wasActive ? 'Clarity off' : 'Clarity enhanced');
      toast.success(wasActive ? '✨ Clarity OFF' : '✨ Clarity ON');
      return;
    }

    if (command.includes('anomaly') || command.includes('detect anomal')) {
      if (isUnlocked) {
        const wasActive = filters?.anomaly_highlight;
        toggleFilter?.('anomaly_highlight');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Anomaly scanner off' : 'Anomaly scanner on');
        toast.success(wasActive ? '🔴 Anomaly scanner OFF' : '🔴 Anomaly scanner ON');
      } else {
        playFeedback('error');
        speakFeedback('Anomaly scanner requires premium');
        toast.error('Anomaly scanner requires Premium');
      }
      return;
    }

    if (command.includes('motion') || command.includes('movement')) {
      if (isUnlocked) {
        const wasActive = filters?.motion_enhance;
        toggleFilter?.('motion_enhance');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Motion enhance off' : 'Motion enhance on');
        toast.success(wasActive ? '🎯 Motion enhance OFF' : '🎯 Motion enhance ON');
      } else {
        playFeedback('error');
        speakFeedback('Motion enhance requires premium');
        toast.error('Motion enhance requires Premium');
      }
      return;
    }

    if (command.includes('track') || command.includes('tracking') || command.includes('follow')) {
      if (isUnlocked) {
        const wasActive = filters?.object_tracking;
        toggleFilter?.('object_tracking');
        playFeedback('filterToggle', !wasActive);
        speakFeedback(wasActive ? 'Object tracking off' : 'Object tracking on');
        toast.success(wasActive ? '🎯 Object tracking OFF' : '🎯 Object tracking ON');
      } else {
        playFeedback('error');
        speakFeedback('Object tracking requires premium');
        toast.error('Object tracking requires Premium');
      }
      return;
    }
    
    // Reset filters
    if (command.includes('reset') || command.includes('clear filter') || command.includes('remove filter')) {
      onReset?.();
      playFeedback('reset');
      speakFeedback('All filters reset');
      toast.success('🔄 Filters reset');
      return;
    }
    
    // Analyze
    if (command.includes('analyze') || command.includes('analyse') || command.includes('scan') || 
        command.includes('detect') || command.includes('identify')) {
      onAnalyze?.();
      playFeedback('analyze');
      speakFeedback('Initiating AI analysis');
      toast.success('🤖 AI analysis started');
      return;
    }
    
    // Fullscreen
    if (command.includes('full screen') || command.includes('fullscreen') || 
        command.includes('immersive') || command.includes('maximize')) {
      onFullscreen?.();
      playFeedback('fullscreen');
      speakFeedback('Fullscreen mode');
      toast.success('🖥️ Fullscreen mode');
      return;
    }
    
    // Switch camera
    if (command.includes('switch camera') || command.includes('flip') || 
        command.includes('front camera') || command.includes('rear camera') ||
        command.includes('selfie') || command.includes('back camera')) {
      onSwitchCamera?.();
      playFeedback('switchCamera');
      speakFeedback('Camera switched');
      toast.success('📷 Camera switched');
      return;
    }

    // Help command
    if (command.includes('help') || command.includes('commands') || command.includes('what can')) {
      playFeedback('success');
      speakFeedback('Available commands: capture, record, stop, zoom in, zoom out, solar, night, thermal, infrared, analyze, fullscreen, switch camera, reset');
      toast.info('🎤 Commands: capture, record, zoom, solar, night, thermal, analyze, fullscreen, switch camera, reset', {
        duration: 5000
      });
      return;
    }
    
    // Unknown command
    playFeedback('error');
    speakFeedback(`Command not recognized: ${command}`);
    toast.info(`Command not recognized: "${command}"`);
  }, [isRecording, zoomLevel, MAX_ZOOM, filters, isUnlocked, onCapture, onStartRecording, 
      onStopRecording, onZoomIn, onZoomOut, setZoomLevel, toggleFilter, onAnalyze, 
      onFullscreen, onSwitchCamera, onReset, playFeedback, speakFeedback]);

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (!isSpeechSupported) {
      audioFeedback.error();
      toast.error('Voice commands not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setTranscript('');
      playFeedback('listeningOff');
      speakFeedback('Voice commands off');
      toast.info('🎤 Voice commands OFF');
    } else {
      try {
        // Initialize audio on first user interaction
        audioFeedback.init();
        recognitionRef.current?.start();
        setIsListening(true);
        playFeedback('listeningOn');
        speakFeedback('Voice commands active. Say help for command list.');
        toast.success('🎤 Voice commands ON - Say a command!');
      } catch (e) {
        console.error('Failed to start recognition:', e);
        playFeedback('error');
        toast.error('Could not start voice recognition');
      }
    }
  }, [isListening, isSpeechSupported, playFeedback, speakFeedback]);

  // Toggle audio feedback
  const toggleAudio = useCallback(() => {
    setAudioEnabled(prev => {
      const newState = !prev;
      if (newState) {
        audioFeedback.success();
      }
      toast.info(newState ? '🔊 Audio feedback ON' : '🔇 Audio feedback OFF');
      return newState;
    });
  }, []);

  if (!isSpeechSupported) {
    return null;
  }

  return (
    <>
      {/* Voice Command Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleListening}
        className="relative p-3 rounded-2xl transition-all"
        style={{
          background: isListening 
            ? 'linear-gradient(145deg, #ff2e2e, #cc0000)' 
            : 'linear-gradient(145deg, rgba(100,110,130,1), rgba(60,70,90,1))',
          boxShadow: isListening 
            ? '0 4px 20px rgba(255,46,46,0.5), inset 0 2px 4px rgba(255,255,255,0.3)' 
            : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.2)',
          border: '2px solid rgba(255,255,255,0.15)'
        }}
        data-testid="voice-command-btn"
      >
        {isListening ? (
          <Mic className="w-5 h-5 text-white animate-pulse" />
        ) : (
          <MicOff className="w-5 h-5 text-white/70" />
        )}
        
        {/* Listening indicator pulse */}
        {isListening && (
          <motion.div
            className="absolute inset-0 rounded-2xl"
            style={{ border: '2px solid #ff2e2e' }}
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
      </motion.button>

      {/* Audio Toggle Button (small) */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={toggleAudio}
        className="p-2 rounded-xl transition-all"
        style={{
          background: audioEnabled 
            ? 'linear-gradient(145deg, rgba(0,242,234,0.2), rgba(0,200,200,0.1))' 
            : 'rgba(255,255,255,0.05)',
          border: audioEnabled ? '1px solid rgba(0,242,234,0.3)' : '1px solid rgba(255,255,255,0.1)'
        }}
        data-testid="audio-toggle-btn"
      >
        {audioEnabled ? (
          <Volume2 className="w-4 h-4" style={{ color: theme?.colors?.primary || '#00f2ea' }} />
        ) : (
          <VolumeX className="w-4 h-4 text-slate-500" />
        )}
      </motion.button>

      {/* Transcript Display */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-2xl backdrop-blur-xl max-w-xs"
            style={{
              background: 'rgba(0,0,0,0.8)',
              border: `2px solid ${isListening ? '#ff2e2e' : theme?.colors?.primary || '#00f2ea'}`,
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}
          >
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                <Volume2 className="w-4 h-4 text-[#ff2e2e]" />
              </motion.div>
              <span className="font-mono text-sm text-white">
                {transcript || 'Listening... Say "help" for commands'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Command Feedback */}
      <AnimatePresence>
        {showFeedback && lastCommand && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-2xl backdrop-blur-xl"
            style={{
              background: 'rgba(0,242,234,0.2)',
              border: '1px solid rgba(0,242,234,0.5)',
              boxShadow: '0 10px 40px rgba(0,242,234,0.2)'
            }}
          >
            <span className="font-mono text-sm text-[#00f2ea]">
              🎤 "{lastCommand}"
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VoiceCommands;
